import { useState, useEffect } from 'react';
import { useQuiz } from '../context/QuizContext';
import { fetchConquistasGlobais } from '../services/quizFirebaseService';
import { Award, Lock, Loader2, ArrowLeft } from 'lucide-react';
import type { Conquista } from '../types/quiz.types';
import { useGameAudio } from '../hooks/useGameAudio';

export function ConquistasScreen() {
  const { state, dispatch } = useQuiz();
  const { playHover, playClick } = useGameAudio();
  const [conquistasDefs, setConquistasDefs] = useState<Conquista[]>([]);
  const [loading, setLoading] = useState(true);

  const desbloqueadas = state.conquistasDesbloqueadas;

  useEffect(() => {
    let isMounted = true;
    fetchConquistasGlobais()
      .then(data => {
        if (isMounted) {
          setConquistasDefs(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("Falha ao puxar Conquistas:", e);
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="h-full max-h-[100dvh] flex flex-col p-3 sm:p-4 w-full max-w-2xl mx-auto animate-in fade-in duration-300 relative z-10 overflow-hidden justify-between">
      
      {/* Header Compacto Fixo */}
      <div className="glass-panel p-2.5 px-3 sm:px-4 rounded-xl mb-3 border-purple-500/30 flex items-center justify-between gap-3 bg-slate-900/80 shadow-[0_0_15px_rgba(168,85,247,0.3)] shrink-0">
        <div className="flex items-center gap-3">
          <button
            onMouseEnter={playHover}
            onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'MENU' }); }}
            className="p-2 rounded-lg bg-slate-800 text-purple-400 hover:bg-purple-500 hover:text-slate-900 transition border border-slate-700 hover:border-purple-400 shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-cyber font-black tracking-wider text-sm sm:text-base uppercase text-purple-400 flex items-center gap-2 drop-shadow-[0_0_5px_rgba(168,85,247,0.5)]">
              <Award size={18} />
              Galeria de Conquistas
            </h1>
            <p className="text-slate-400 text-[10px] sm:text-xs">Medalhas e honrarias da Biografia Preta.</p>
          </div>
        </div>
        
        <div className="px-2.5 py-1 bg-slate-950 border border-purple-500/40 rounded-full text-xs font-cyber font-bold text-purple-300 shrink-0">
          {desbloqueadas.length} / {conquistasDefs.length || 3}
        </div>
      </div>

      {/* Lista Interna Contida com Fading de Borda */}
      <div className="flex-1 overflow-y-auto custom-scrollbar scroll-edge-fade pr-1 pb-2 space-y-2.5 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8 glass-panel rounded-xl border-purple-500/20 my-auto">
            <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
            <p className="font-cyber uppercase tracking-widest text-purple-400/60 text-xs animate-pulse">Carregando Galeria...</p>
          </div>
        ) : (
          conquistasDefs.map((c) => {
            const ok = desbloqueadas.includes(c.id);
            return (
              <div
                key={c.id}
                className={`rounded-xl p-3 sm:p-4 border flex items-center gap-3.5 transition-all ${
                  ok
                    ? 'glass-panel border-purple-500/40 bg-purple-950/20 text-purple-100 shadow-[inset_0_0_12px_rgba(168,85,247,0.15)]'
                    : 'bg-slate-900/60 border-slate-800/80 opacity-60'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                  ok ? 'bg-purple-900/40 border-purple-500/50 text-purple-300' : 'bg-slate-950 border-slate-800 text-slate-600'
                }`}>
                  {ok ? <Award className="w-5 h-5 drop-shadow-[0_0_5px_rgba(168,85,247,0.8)]" /> : <Lock className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-cyber font-bold text-sm text-white truncate">{c.nome}</h3>
                    {ok && <span className="text-[10px] font-cyber font-bold text-emerald-400 uppercase tracking-widest shrink-0 ml-2">Desbloqueada</span>}
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5 leading-snug truncate">{c.descricao}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        onMouseEnter={playHover}
        onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'MENU' }); }}
        className="btn-cyber w-full py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 font-cyber font-bold tracking-wider hover:text-white hover:bg-slate-700 transition uppercase text-xs shrink-0 mt-2"
      >
        Voltar ao Menu Principal
      </button>
    </div>
  );
}
